import { HitsPerPage } from 'react-instantsearch';
import Image from 'next/image';

import { RichInline } from '@/atoms';
import { TrendingSearches } from '@/molecules/trending-searches';

// Import ESTÁTICO a propósito: con `next/dynamic` (aun con ssr:true) el
// subárbol se hidrata vacío hasta que llega el chunk — el hero SSR colapsaba
// a 0px y reaparecía, un salto de layout (CLS 0.38) visible en cada carga.
import InstantSearch from '@/components/algolia/instant-search';
import { SearchBoxHero } from '@/components/algolia/search-box-hero';
import { getRealUrl } from '@/utils/utils';

import type { TypeTrendingSearchItem } from '@/molecules/trending-searches';
import type { FunctionComponent } from 'react';

export interface TypeHeroCenteredProps {
  backgroundImage?: { url?: string } | null;
  eyebrow?: string;
  title?: string;
  overlay?: number;
  instantSearchSettings?: {
    Indice: string;
    NumeroResultados?: number;
    TextoBuscador?: string;
    TipoVivienda?: string;
  } | null;
  /** Bloque "Lo más buscado". */
  trending?: {
    Titulo?: string;
    Items?: TypeTrendingSearchItem[];
  } | null;
}

/** Variante centered del hero (Figma HoM): imagen + overlay + h1 + buscador + trending. */
export const HeroCentered: FunctionComponent<TypeHeroCenteredProps> = ({
  backgroundImage,
  eyebrow,
  title,
  overlay = 0.4,
  instantSearchSettings,
  trending,
}: TypeHeroCenteredProps) => {
  const bgUrl = backgroundImage?.url
    ? getRealUrl(backgroundImage.url)
    : undefined;

  const indexName = instantSearchSettings?.Indice
    ? `development_api::zfm-disponiblidad.${instantSearchSettings.Indice.replaceAll(
        '_',
        '-',
      )}`
    : undefined;

  const renderSearch = () => {
    if (!indexName) return null;
    return (
      <InstantSearch indexName={indexName} ssrPlaceholder>
        <SearchBoxHero
          locationPlaceholder={instantSearchSettings?.TextoBuscador}
          typeFlat={instantSearchSettings?.TipoVivienda}
        />
        <HitsPerPage
          items={[
            {
              label: '',
              value: instantSearchSettings?.NumeroResultados ?? 12,
              default: true,
            },
          ]}
          className="hidden"
        />
      </InstantSearch>
    );
  };

  return (
    <section className="relative flex min-h-[560px] flex-col justify-end overflow-hidden lg:min-h-screen">
      {bgUrl && (
        <Image
          src={bgUrl}
          alt=""
          fill
          priority
          sizes="100vw"
          className="-z-20 object-cover"
        />
      )}

      <div
        aria-hidden
        className="absolute inset-0 -z-10 bg-gradient-to-b from-black to-transparent"
        style={{ opacity: overlay }}
      />

      {/* Encabezado respeta `--wrapper-size` del tenant; search box + trending van a 1600px porque los 4 filtros no caben en wrappers estándar. */}
      <div className="mx-auto flex w-full flex-col items-center gap-12 px-4 pt-24 pb-10 lg:gap-20 lg:px-16 lg:pt-40 lg:pb-10">
        <div className="flex w-full max-w-(--wrapper-size) flex-col items-center gap-4 text-center">
          {eyebrow && (
            <p className="text-headline text-headline-h3 text-neutrals-0">
              {eyebrow}
            </p>
          )}
          {title && (
            <RichInline
              as="h1"
              html={title}
              className="text-display text-headline-h1 md:text-display text-neutrals-0"
            />
          )}
        </div>

        <div className="flex w-full max-w-[1600px] flex-col items-center gap-8">
          {renderSearch()}

          {trending?.Items && trending.Items.length > 0 && (
            <TrendingSearches title={trending.Titulo} items={trending.Items} />
          )}
        </div>
      </div>
    </section>
  );
};

HeroCentered.displayName = 'HeroCentered';
